import { openSplitViewState, toast } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { contentOcrNotificationState, ocrNotificationPopupOpenState } from 'modules/request';
import { useCallback, useEffect, useState } from 'react';
import { useMount, usePrevious, useToggle } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { GetOcr, useGetRequestVersion } from 'shared/data/webApp/v2';
import { ocrDocumentIsEmpty } from 'shared/helpers';
import { useCheckOcrStatus } from 'shared/hooks/useCheckOcrStatus';
import { useStopCheckOcrStatus } from 'shared/hooks/useStopCheckOcrStatus';
import { ContentSplitViewMode, contentSplitViewState, ocrIsLoadingState } from 'shared/states';

import { getActiveRequestSafe } from '../../selectors/pageSelectors';
import { getModifiedRequest } from './PageContent.helpers';
import { messages } from './PageContent.messages';
import { UseRequestVersionHistoryOptions, UseStarterGuideParams } from './PageContent.types';

export const useRequestVersionHistory = ({ companyId, request }: UseRequestVersionHistoryOptions) => {
    const [selectedVersion, setSelectedVersion] = useState<domain.RequestVersionExtended | null>(null);
    const [modifiedRequest, setModifiedRequest] = useState<selectors.types.ExpandedRequest | null>(null);

    const contentSplitView = useAtomValue(contentSplitViewState);

    const activeRequestVersion =
        (contentSplitView.mode === ContentSplitViewMode.VersionHistory && contentSplitView.activeRequestVersion) ||
        undefined;

    const version = selectedVersion?.version
        ? selectedVersion.version !== activeRequestVersion
            ? selectedVersion.version
            : undefined
        : undefined;

    const { data: requestVersion } = useGetRequestVersion({
        path: {
            requestId: request?.id,
            companyId,
            requestVersion: version,
        },
    });

    useEffect(() => {
        if (request && requestVersion?.snapshot) {
            const modifiedRequest = getModifiedRequest(request, requestVersion.snapshot);

            setModifiedRequest(modifiedRequest);
        }
    }, [request, requestVersion]);

    return {
        selectedVersion,
        setSelectedVersion,
        modifiedRequest,
        setModifiedRequest,
    };
};

export const useOcrStatusHandler = (activeRequestId: string | null) => {
    // TODO: Fix additional rerender due to new object reference
    const request = useSelector(getActiveRequestSafe);
    const setOcrNotificationPopupOpen = useSetAtom(ocrNotificationPopupOpenState);
    const setContentOcrNotification = useSetAtom(contentOcrNotificationState);
    const ocrAttachment = request?.attachments.find((attachment) => attachment.isOcrOngoing);
    const successCallback = useCallback(
        (data: GetOcr['response']) => {
            if (ocrDocumentIsEmpty(data.document)) {
                setContentOcrNotification({
                    buttonText: messages.ocrDocumentIsEmptyButtonText,
                    description: messages.ocrDocumentIsEmptyText({
                        fileName: ocrAttachment?.name || '',
                    }),
                });
                setOcrNotificationPopupOpen(true);
            } else {
                toast.success(messages.successRecognitionText);
            }
        },
        [ocrAttachment?.name, setContentOcrNotification, setOcrNotificationPopupOpen]
    );
    const errorCallback = useCallback(
        (errorMessage?: string) => {
            setContentOcrNotification({
                buttonText: messages.errorRecognitionButtonText,
                description: errorMessage || messages.errorRecognitionText,
            });
            setOcrNotificationPopupOpen(true);
        },
        [setOcrNotificationPopupOpen, setContentOcrNotification]
    );
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const { checkOcrStatus, isOcrAttachmentInProgress, isFetched } = useCheckOcrStatus(
        request,
        ocrAttachment?.id,
        successCallback,
        errorCallback
    );
    const stopCheckOcrStatus = useStopCheckOcrStatus();

    useEffect(() => {
        if (
            request &&
            request.attachments.some((attachment) => attachment.isOcrOngoing) &&
            !ocrIsLoading &&
            (!isFetched || isOcrAttachmentInProgress)
        ) {
            checkOcrStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkOcrStatus, activeRequestId, stopCheckOcrStatus, ocrIsLoading, isOcrAttachmentInProgress]);

    // Stop checking ocr status if request is changed or page is unmounted
    useEffect(() => {
        return () => {
            stopCheckOcrStatus();
        };
    }, [activeRequestId, stopCheckOcrStatus]);
};

export const useSplitViewOnNextRequest = (nextRequest: selectors.types.ExpandedRequest | null) => {
    const openSplitView = useAtomValue(openSplitViewState);
    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);
    const prevRequest = usePrevious(nextRequest);

    useEffect(() => {
        if (openSplitView && nextRequest?.id !== prevRequest?.id) {
            const nextRequestIsBatch = nextRequest?.integrationCode
                ? [
                      domain.IntegrationCode.XeroAmaxPayBatchPayment,
                      domain.IntegrationCode.XeroAirwallexBatchPayment,
                      domain.IntegrationCode.XeroBillBatchPayment,
                  ].includes(nextRequest?.integrationCode)
                : false;

            if (
                contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment &&
                !nextRequestIsBatch
            ) {
                setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
            }

            if (
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment &&
                nextRequestIsBatch &&
                nextRequest
            ) {
                const nextBillId =
                    'items' in nextRequest.details && nextRequest.details.items[0]?.xeroBillInvoiceRequestId;

                nextBillId &&
                    setContentSplitView({
                        mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                        billId: nextBillId,
                    });
            } else if (
                contentSplitView.mode === ContentSplitViewMode.NetSuiteBillLinkedToBillPayment &&
                nextRequest?.integrationCode === domain.IntegrationCode.NetSuiteBillPayment
            ) {
                const nextBillId = nextRequest.details.applyList[0]?.requestId;

                nextBillId &&
                    setContentSplitView({
                        mode: ContentSplitViewMode.NetSuiteBillLinkedToBillPayment,
                        billId: nextBillId,
                    });
            } else {
                setContentSplitView({
                    mode: ContentSplitViewMode.AttachmentsToRequest,
                    attachmentId: nextRequest?.attachments[0]?.id || '',
                });
            }
        }
    }, [
        contentSplitView.mode,
        openSplitView,
        prevRequest,
        nextRequest,
        setContentSplitView,
        setSplitViewGoToBarSettings,
    ]);
};

export const useStarterGuide = (params: UseStarterGuideParams) => {
    const { isEmptyList, profileId, companyId, openTour } = params;

    const [open, toggleOpen] = useToggle(false);

    const [gettingStarted, _, cleanGettingStarted] = hooks.useQueryParam('gettingStarted');

    const onCloseStarterGuide = useCallback(() => {
        toggleOpen(false);
    }, [toggleOpen]);

    useMount(() => {
        if (!gettingStarted) {
            return;
        }

        cleanGettingStarted();
        amplitudeService.sendData('requests: land on getting started tutorial', {
            user_id: profileId,
            org_id: companyId,
            type: isEmptyList ? 'no_requests' : 'with_request',
        });

        if (isEmptyList) {
            toggleOpen(true);
        } else if (openTour) {
            openTour();
        }
    });

    return {
        isOpenStarterGuide: open,
        onToggleStarterGuide: toggleOpen,
        onCloseStarterGuide,
    };
};
